/**
 * First-party analytics client. Queues events and posts them in batches to
 * POST /api/public/events, which forwards them to Cloud Logging / BigQuery.
 * Runs alongside GA4; `window.disableTracking` (set synchronously in
 * index.html from the vboardDisableTracking cookie / URL param) turns every
 * function here into a no-op.
 *
 * The client never sends user ids: anonymous visitors get a persistent random
 * id (localStorage `vb_aid`), logged-in users are tagged server-side from the
 * session. The server always answers 204 and validates on its own, so the
 * response is never read and errors are swallowed without retry.
 *
 * Must keep working on Chrome < 90 (whiteboard clients): no crypto.randomUUID
 * guaranteed, storage may throw (Safari private mode).
 */

const ENDPOINT = '/api/public/events';
const QUEUE_CAP = 200;
const FLUSH_AT = 20;
const FLUSH_DELAY_MS = 5000;
const BATCH_SIZE = 25;
const MAX_LEN = 256;
const ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;
const LANG_SEGMENT = /^\/(ro|en|fr|it)(?=\/|$)/;
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX32_SEGMENT = /^[0-9a-f]{32}$/i;
const NUMERIC_SEGMENT = /^\d+$/;

const ANON_KEY = 'vb_aid';
const SESSION_KEY = 'vb_sid';
const REF_SENT_KEY = 'vb_ref_sent';

let queue = [];
let flushTimer = null;
// In-memory fallbacks when web storage throws.
const memory = {};

const isDisabled = () => window.disableTracking === true;

const randomHex32 = () => {
    let out = '';
    for (let i = 0; i < 32; i++) {
        out += Math.floor(Math.random() * 16).toString(16);
    }
    return out;
};

const generateId = () => {
    try {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }
    } catch (e) {
        // fall through to Math.random
    }
    return randomHex32();
};

const storageGet = (storage, key) => {
    try {
        return window[storage].getItem(key);
    } catch (e) {
        return null;
    }
};

const storageSet = (storage, key, value) => {
    try {
        window[storage].setItem(key, value);
        return true;
    } catch (e) {
        return false;
    }
};

const getOrCreateId = (storage, key) => {
    let id = storageGet(storage, key);
    if (id && ID_PATTERN.test(id)) {
        return id;
    }
    if (memory[key] && ID_PATTERN.test(memory[key])) {
        return memory[key];
    }
    id = generateId();
    if (!storageSet(storage, key, id)) {
        memory[key] = id;
    }
    return id;
};

const anonId = () => getOrCreateId('localStorage', ANON_KEY);
const sessionId = () => getOrCreateId('sessionStorage', SESSION_KEY);

const normalizePath = (pathname) => {
    let path = typeof pathname === 'string' ? pathname : '/';
    if (path.charAt(0) !== '/') {
        path = '/' + path;
    }
    path = path.replace(LANG_SEGMENT, '');
    if (path === '') {
        return '/';
    }
    path = path
        .split('/')
        .map((segment) => {
            if (UUID_SEGMENT.test(segment) || HEX32_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment)) {
                return ':id';
            }
            return segment;
        })
        .join('/');
    if (path.length > 1 && path.charAt(path.length - 1) === '/') {
        path = path.slice(0, -1);
    }
    if (path === '') {
        path = '/';
    }
    return path.slice(0, MAX_LEN);
};

// Computed per event: window.isTeams is null for up to 5 s after load.
const platform = () => {
    if (window.isTeams === true) {
        return 'teams';
    }
    try {
        if (
            (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
            window.navigator.standalone === true
        ) {
            return 'pwa';
        }
    } catch (e) {
        // matchMedia unavailable
    }
    return 'web';
};

const screenClass = () => {
    const w = window.innerWidth;
    if (w < 600) return 'xs';
    if (w < 960) return 'sm';
    if (w < 1280) return 'md';
    if (w < 1920) return 'lg';
    return 'xl';
};

const referrer = () => {
    const ref = document.referrer;
    if (!ref) {
        return undefined;
    }
    try {
        const url = new URL(ref);
        return (url.origin + url.pathname).slice(0, MAX_LEN);
    } catch (e) {
        return ref.split('?')[0].split('#')[0].slice(0, MAX_LEN);
    }
};

const clearTimer = () => {
    if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
};

const scheduleFlush = () => {
    if (flushTimer !== null) {
        return;
    }
    flushTimer = setTimeout(() => {
        flushTimer = null;
        flush();
    }, FLUSH_DELAY_MS);
};

const send = (body, beacon) => {
    try {
        if (beacon && window.navigator && typeof window.navigator.sendBeacon === 'function') {
            const ok = window.navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
            if (ok) {
                return;
            }
        }
        if (typeof window.fetch === 'function') {
            window
                .fetch(ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                    keepalive: true,
                    credentials: 'same-origin',
                })
                .catch(() => {});
        }
    } catch (e) {
        // never let analytics break the app
    }
};

const flush = ({ beacon } = {}) => {
    clearTimer();
    if (isDisabled()) {
        queue = [];
        return;
    }
    while (queue.length > 0) {
        const events = queue.splice(0, BATCH_SIZE);
        send(JSON.stringify({ events }), beacon === true);
    }
};

const enqueue = (event) => {
    const clean = {};
    Object.keys(event).forEach((key) => {
        const v = event[key];
        if (v !== undefined && v !== null && v !== '') {
            clean[key] = v;
        }
    });
    queue.push(clean);
    if (queue.length > QUEUE_CAP) {
        queue.splice(0, queue.length - QUEUE_CAP);
    }
    if (queue.length >= FLUSH_AT) {
        flush();
    } else {
        scheduleFlush();
    }
};

// `extra` carries top-level fields the server knows (e.g. referrer); `props` is free-form.
const track = (category, action, label, value, props, extra) => {
    if (isDisabled() || !category || !action) {
        return;
    }
    try {
        enqueue({
            ...(extra || {}),
            category,
            action,
            label,
            value,
            page: normalizePath(window.location.pathname),
            props: props && typeof props === 'object' ? JSON.stringify(props) : undefined,
            anonId: anonId(),
            sessionId: sessionId(),
            lang: storageGet('localStorage', 'language'),
            platform: platform(),
            screen: screenClass(),
            clientTs: new Date().toISOString(),
        });
    } catch (e) {
        // never let analytics break the app
    }
};

const trackPageView = (pathname) => {
    if (isDisabled()) {
        return;
    }
    let extra;
    if (storageGet('sessionStorage', REF_SENT_KEY) !== '1' && !memory[REF_SENT_KEY]) {
        if (!storageSet('sessionStorage', REF_SENT_KEY, '1')) {
            memory[REF_SENT_KEY] = '1';
        }
        const ref = referrer();
        if (ref) {
            extra = { referrer: ref };
        }
    }
    track('page', 'view', normalizePath(pathname), undefined, undefined, extra);
};

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flush({ beacon: true });
        }
    });
    window.addEventListener('pagehide', () => {
        flush({ beacon: true });
    });
}

export default { track, trackPageView, flush };
